import { useGetChannelsDetailsSuspenseQuery } from "#features/channels/api/useGetChannelsDetailsSuspenseQuery.ts";
import { useMemo } from "react";

/**
 * Hook to get the list of connected apps, separated into connected personal integrations and
 * available Workleap platform apps.
 * This ensures consistency between the Popover and sidepanel sections.
 *
 * @returns An object containing:
 *   - connectedApps: Connected `ConnectorChannelType` values from
 *     `personalIntegrations.filter(integration => integration.isConnected)`
 *   - allApps: `connectedApps`, plus `workleapChannelTypes`, and `"WebCrawler"` when websites are available
 *   - hasAvailableAppsToConnect: Boolean indicating if there are apps available to connect
 */
export const useConnectedExternalApps = () => {
    const { data: {
        workleapChannelTypes,
        websites,
        personalIntegrations
    } } = useGetChannelsDetailsSuspenseQuery();

    const connectedApps = useMemo(() => {
        return [
            ...personalIntegrations.filter(x => x.isConnected).map(x => x.channelType)
        ];
    }, [personalIntegrations]);

    const allApps = useMemo(() => {
        const apps = [...connectedApps, ...workleapChannelTypes];

        if (websites.length > 0) {
            apps.push("WebCrawler");
        }

        return apps;
    }, [connectedApps, websites.length, workleapChannelTypes]);

    const hasAvailableAppsToConnect = useMemo(() => {
        return personalIntegrations.some(integration => integration.canConnect);
    }, [personalIntegrations]);

    return {
        connectedApps,
        allApps,
        hasAvailableAppsToConnect
    };
};
